import {
  CrystalType,
  DEFAULT_CRYSTAL_TYPE,
} from "@/components/product/CrystalSelector";
import {
  PointCloudTransform,
  DEFAULT_TRANSFORM,
} from "@/components/product/PointCloudControls";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import * as THREE from "three";

// 渲染模式枚举 - 水晶材质类型选择
export type RenderMode = "hybrid";
export const DEFAULT_RENDER_MODE: RenderMode = "hybrid";

// 水晶尺寸信息接口
export interface CrystalDimensions {
  width: number;    // 宽度
  height: number;   // 高度
  depth: number;    // 深度
  maxDimension: number;  // 最大维度（用于缩放计算）
  scaleRatio: number;    // 缩放比例
}

// 默认水晶尺寸（占位值）
export const DEFAULT_CRYSTAL_DIMENSIONS: CrystalDimensions = {
  width: 1.5,
  height: 1.5,
  depth: 1.5,
  maxDimension: 1.5,
  scaleRatio: 1.0,
};

/**
 * 极简水晶配置 - 只包含真正需要跨组件共享的状态
 */
export interface SimpleCrystalConfig {
  crystalType: CrystalType;
  text: string;
  selectedFont: string;
  transform: PointCloudTransform;
  pointCloudGeometry: THREE.BufferGeometry | null; // 第5个共享状态：生成的点云几何体
  renderMode: RenderMode; // 第6个共享状态：渲染模式选择
  crystalDimensions: CrystalDimensions; // 第7个共享状态：水晶实际尺寸信息
}

/**
 * 极简默认配置
 */
export const DEFAULT_SIMPLE_CONFIG: SimpleCrystalConfig = {
  crystalType: DEFAULT_CRYSTAL_TYPE,
  text: "HELLO",
  selectedFont: "Arial",
  transform: DEFAULT_TRANSFORM,
  pointCloudGeometry: null,
  renderMode: DEFAULT_RENDER_MODE, // 默认使用透射材质
  crystalDimensions: DEFAULT_CRYSTAL_DIMENSIONS, // 默认水晶尺寸
};

/**
 * 极简Context状态接口 - 只包含核心共享状态
 */
interface SimpleCrystalCustomizerState {
  // 核心共享配置
  config: SimpleCrystalConfig;

  // 核心操作函数
  setText: (text: string) => void;
  setSelectedFont: (font: string) => void;
  setCrystalType: (type: CrystalType) => void;
  setTransform: (transform: PointCloudTransform) => void;
  setPointCloudGeometry: (geometry: THREE.BufferGeometry | null) => void;
  setRenderMode: (mode: RenderMode) => void;
  setCrystalDimensions: (dimensions: CrystalDimensions) => void;
}

const CrystalCustomizerContext = createContext<SimpleCrystalCustomizerState | null>(
  null,
);

// Context Provider组件
interface CrystalCustomizerProviderProps {
  children: ReactNode;
}

/**
 * 极简Context Provider - 只管理核心共享状态
 */
export function CrystalCustomizerProvider({
  children,
}: CrystalCustomizerProviderProps) {
  // 极简状态管理
  const [config, setConfig] = useState<SimpleCrystalConfig>(DEFAULT_SIMPLE_CONFIG);

  // 极简状态更新函数
  const setText = useCallback((text: string) => {
    setConfig((prev) => ({ ...prev, text }));
  }, []);

  const setSelectedFont = useCallback((selectedFont: string) => {
    setConfig((prev) => ({ ...prev, selectedFont }));
  }, []);

  const setCrystalType = useCallback((crystalType: CrystalType) => {
    setConfig((prev) => ({ ...prev, crystalType }));
  }, []);

  const setTransform = useCallback((transform: PointCloudTransform) => {
    setConfig((prev) => ({ ...prev, transform }));
  }, []);

  const setPointCloudGeometry = useCallback((pointCloudGeometry: THREE.BufferGeometry | null) => {
    setConfig((prev) => {
      // 如果存在旧的几何体，先清理
      if (prev.pointCloudGeometry) {
        prev.pointCloudGeometry.dispose();
      }
      return { ...prev, pointCloudGeometry };
    });
  }, []);

  const setRenderMode = useCallback((renderMode: RenderMode) => {
    setConfig((prev) => ({ ...prev, renderMode }));
  }, []);

  const setCrystalDimensions = useCallback((crystalDimensions: CrystalDimensions) => {
    setConfig((prev) => ({ ...prev, crystalDimensions }));
  }, []);

  // 极简Context值 - 只包含核心共享状态
  const value = useMemo(
    (): SimpleCrystalCustomizerState => ({
      config,
      setText,
      setSelectedFont,
      setCrystalType,
      setTransform,
      setPointCloudGeometry,
      setRenderMode,
      setCrystalDimensions,
    }),
    [config, setText, setSelectedFont, setCrystalType, setTransform, setPointCloudGeometry, setRenderMode, setCrystalDimensions],
  );

  return (
    <CrystalCustomizerContext.Provider value={value}>
      {children}
    </CrystalCustomizerContext.Provider>
  );
}

/**
 * 极简Context消费Hook
 */
export function useCrystalCustomizer(): SimpleCrystalCustomizerState {
  const context = useContext(CrystalCustomizerContext);
  if (!context) {
    throw new Error(
      "useCrystalCustomizer must be used within a CrystalCustomizerProvider",
    );
  }
  return context;
}